import React, { useState, useCallback, useEffect } from 'react';
import { useAIView } from '@/context/AIViewContext';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { AIChatContent } from './AIChatContent';
import { cn } from '@/lib/core/utils';
import { SimpleTooltip } from '@/components/overlays/simple-tooltip';

interface AIRightSidebarProps {
  businessId: string | null;
  /** Mode: 'assistant' for dashboard (full features), 'sales' for landing page (minimal) */
  mode?: 'assistant' | 'sales';
}

const MIN_WIDTH = 320;
const MAX_WIDTH = 800;
const DEFAULT_WIDTH = 420;
const STORAGE_KEY = 'ai_sidebar_width';

export const AIRightSidebar: React.FC<AIRightSidebarProps> = ({ businessId, mode = 'assistant' }) => {
  const { viewMode, close, openFloating } = useAIView();
  const isMobile = useIsMobile();

  const isOpen = viewMode === 'sidebar';

  // Resizable width state - persist to localStorage
  const [width, setWidth] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) {
          return parsed;
        }
      }
    } catch {
      // Ignore localStorage errors
    }
    return DEFAULT_WIDTH;
  });

  const [isResizing, setIsResizing] = useState(false);

  // Handle resize via mouse events
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  // Double-click to reset to default width
  const handleDoubleClick = useCallback(() => {
    setWidth(DEFAULT_WIDTH);
    try {
      localStorage.setItem(STORAGE_KEY, String(DEFAULT_WIDTH));
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Calculate width from right edge of viewport
      const newWidth = window.innerWidth - e.clientX;
      setWidth(Math.max(MIN_WIDTH, Math.min(newWidth, MAX_WIDTH)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    // Add cursor style to body during resize
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Save width to localStorage when done resizing
  useEffect(() => {
    if (isResizing) return;
    try {
      localStorage.setItem(STORAGE_KEY, String(width));
    } catch {
      // Ignore localStorage errors
    }
  }, [width, isResizing]);

  const handleToggleFloating = () => {
    openFloating();
  };

  // Don't render on mobile
  if (isMobile) return null;

  return (
    <div
      className="hidden md:block text-sidebar-foreground"
      data-state={isOpen ? 'open' : 'closed'}
    >
      {/* Spacer - creates space for the sidebar, pushing content left */}
      {isOpen && (
        <div
          className={cn(
            'relative h-svh bg-transparent transition-[width] duration-200 ease-linear',
            isResizing && 'transition-none'
          )}
          style={{ width: `${width}px` }}
        />
      )}

      {/* Fixed sidebar panel */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 h-svh transition-[width] duration-200 ease-linear',
          'border-l border-border bg-sidebar',
          isResizing && 'transition-none',
          !isOpen && 'overflow-hidden'
        )}
        style={{ width: isOpen ? `${width}px` : 0 }}
      >
        {/* Resize handle */}
        <div
          className={cn(
            'absolute inset-y-0 left-0 w-1 cursor-ew-resize z-10',
            'hover:bg-primary/50 transition-colors',
            isResizing && 'bg-primary/50'
          )}
          onMouseDown={handleResizeStart}
          onDoubleClick={handleDoubleClick}
        />

        <div
          data-sidebar="sidebar"
          className={cn(
            "flex h-full flex-col bg-sidebar",
            // Shrink/expand animation on the content
            "transition-all duration-300 ease-out origin-right",
            isOpen ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'
          )}
          style={{ width: `${width}px` }}
        >
          <AIChatContent
            businessId={businessId}
            variant="sidebar"
            mode={mode}
            onClose={close}
            onToggleFullscreen={handleToggleFloating}
          />
        </div>
      </div>
    </div>
  );
};

export default AIRightSidebar;
